import {
  Directive,
  effect,
  inject,
  Injector,
  input,
  type OnInit,
  output,
  runInInjectionContext,
  signal,
} from '@angular/core';
import type { PctFormGroup } from '@pct-ng/form';
import { debounceTime, map } from 'rxjs/operators';
import type { AbstractControl } from '@angular/forms';
import { Store } from '@ngxs/store';
import { TableStateSelectors } from '@pct-ng/feature-table-material';
import { isNil } from '@pct-ts/typeguard';
import { deepDistinctUntilChanged } from '@pct/rxjs';

@Directive({})
export abstract class AbstractListFilterPanelComponent<
  FORM extends { [K in keyof FORM]: AbstractControl<any, any> },
  LIST_FILTERS_TO_BACKEND,
> implements OnInit {
  readonly #injector = inject(Injector);

  protected readonly isExpandedFilterPanel = signal(false);
  protected form!: PctFormGroup<FORM>;

  readonly i18nParentPrefix = input.required<string>();
  readonly tableId = input.required<string>();

  readonly changeForm = output<LIST_FILTERS_TO_BACKEND>();

  ngOnInit(): void {
    this.form = this.createForm();
    this.form.valueChanges
      .pipe(
        debounceTime(500),
        deepDistinctUntilChanged(),
        map(value => {
          if (isNil(value)) {
            return value;
          }
          return Object.entries(value).reduce(
            (obj, [key, val]) => ({
              ...obj,
              [key]: val === null ? undefined : val,
            }),
            {} as any,
          ) as LIST_FILTERS_TO_BACKEND;
        }),
      )
      .subscribe(value => {
        if (this.form.valid) {
          this.changeForm.emit(value);
        }
      });

    this.loadFromState();
  }

  protected loadFromState(): void {
    runInInjectionContext(this.#injector, () => {
      const store = inject(Store);
      const ref = effect(() => {
        const state: any = store.selectSnapshot(TableStateSelectors.getFilters(this.tableId()));
        if (!isNil(state)) {
          // Replace backend undefined to reactive forms null
          const patchValue = Object.entries(state as any).reduce(
            (obj, [key, val]) => ({
              ...obj,
              [key]: val === undefined ? null : val,
            }),
            {} as any,
          ) as any;
          this.form.patchValue(patchValue, { emitEvent: false });

          if (
            !(Object.keys(patchValue).length === 1 && !isNil(patchValue['searchText'])) &&
            Object.values(patchValue).some(val => !isNil(val)) &&
            this.isExpandedFilterPanel() === false
          ) {
            this.isExpandedFilterPanel.set(true);
          }
        }
        ref.destroy();
      });
    });
  }

  protected abstract createForm(): PctFormGroup<FORM>;
}
